import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "urql";
import PaperComponent from "@mui/material/Paper";
import {
  Typography as TypographyComponent,
  Button as ButtonComponent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Container,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { useNotification } from "../hooks/useNotification";

import ContentContainer from "./ContentContainer";
import { useAtom } from "jotai";
import { logoutAtom } from "../support/atoms";
import {
  logoutAllMutation,
  logoutMutation,
  updatePasswordQuery,
  updateUserMutation,
  userInfoQuery,
} from "../support/mutations";
import { getRefreshToken } from "../support/graphqlApi";

const icons = {
  DESKTOP: <DesktopMacIcon />,
  SMARTPHONE: <PhoneAndroidIcon />,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: 30,
  [theme.breakpoints.down("xs")]: {
    marginTop: 50,
  },
}));

const Paper = styled(PaperComponent)(({ theme }) => ({
  background: theme.palette.primary.paper,
  padding: 15,
  marginBottom: 15,
}));

const Typography = styled(TypographyComponent)(() => ({
  marginBottom: 10,
}));

const ButtonLong = styled(ButtonComponent)(() => ({
  margin: 5,
  marginTop: 10,
  width: 230,
}));
const Button = styled(ButtonComponent)(() => ({
  margin: 5,
  width: 120,
}));

const Settings = () => {
  const [, logout] = useAtom(logoutAtom);
  const [passwords, setPasswords] = useState({ currPass: "", newPass: "" });
  const [currentUser, setCurrentUser] = useState({});

  const [userInfoResult, fetchUserInfo] = useQuery({ query: userInfoQuery });
  const [updatePassResult, updatePassword] = useMutation(updatePasswordQuery);
  const [, logOutSession] = useMutation(logoutMutation);
  const [, logoutAll] = useMutation(logoutAllMutation);
  const [updateUserResult, updateUser] = useMutation(updateUserMutation);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    const currentUser = userInfoResult?.data?.currentUser;
    delete currentUser?.__typename;
    setCurrentUser(currentUser);
  }, [userInfoResult?.data?.currentUser]);

  const updatePass = () => {
    const { currPass, newPass } = passwords;
    if (currPass && newPass && currPass !== newPass)
      updatePassword({ currPass, newPass }).then(async (result) => {
        if (result.data) {
          showNotification("Password has successfully been changed", {
            severity: "success",
            autoHideDuration: 3000,
          });
          logout();
        } else if (result.error)
          showNotification(result.error.message, {
            severity: "error",
            autoHideDuration: 3000,
          });
      });
  };

  const updateUserInfo = () => {
    updateUser({ updateUserInput: currentUser }).then(async (result) => {
      if (result.data) {
        showNotification("User profile has been successfully updated", {
          severity: "success",
          autoHideDuration: 3000,
        });
      } else if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      }
    });
  };

  const logoutAllDevices = () => {
    logoutAll().then(async (result) => {
      if (result.data) {
        showNotification("Successfully logged out all devices", {
          severity: "success",
          autoHideDuration: 3000,
        });
        logout();
      } else if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      }
    });
  };

  const logOutSingle = (item) => {
    logOutSession({ refreshToken: item.refreshToken }).then(async (result) => {
      if (result.data) {
        showNotification("Successfully logged out device", {
          severity: "success",
          autoHideDuration: 3000,
        });
        fetchUserInfo({ requestPolicy: "network-only" });
      } else if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      }
    });
  };

  const deviceList = (
    <List>
      {userInfoResult?.data?.sessions?.map((item, index) => (
        <ListItem button key={item.refreshToken}>
          <ListItemIcon>
            {icons[item.deviceType?.toUpperCase()] || <DesktopMacIcon />}
          </ListItemIcon>
          <ListItemText
            sx={{
              "& .MuiListItemText-primary": {
                fontSize: "0.85rem",
                lineHeight: 1.2,
              },
            }}
            primary={`${item.deviceModel || item.deviceOs} on ${item.deviceClient} from ${item.ip || "localhost"}${getRefreshToken() === item.refreshToken ? " (this device)" : ""}`}
          />
          <IconButton color="primary" onClick={() => logOutSingle(item)}>
            <CloseIcon />
          </IconButton>
        </ListItem>
      ))}
    </List>
  );

  return (
    <StyledContainer>
      <Paper elevation={2}>
        <Typography variant="h5">Devices</Typography>
        {deviceList}
        {userInfoResult?.data?.sessions?.length > 0 ? (
          <ButtonLong
            color="primary"
            variant="contained"
            onClick={() => logoutAllDevices()}
          >
            Log out all devices
          </ButtonLong>
        ) : (
          ""
        )}
      </Paper>
    </StyledContainer>
  );
};

export default Settings;
